import { Helmet } from "react-helmet-async";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { motion } from "framer-motion";
import { ArrowRight, Brain, BarChart3, Shield, Gauge, Fuel, Zap } from "lucide-react";
import { Link } from "react-router-dom";

import { useTranslation } from "react-i18next";
import aboutHero from "@/assets/about-hero.webp";

const ease = [0.16, 1, 0.3, 1] as const;

const capabilityIcons = [Brain, Fuel, Gauge, BarChart3, Shield, Zap];

const About = () => {
  
  const { t } = useTranslation("about");

  const capabilities = [
    { icon: capabilityIcons[0], title: t("capabilities.aiTitle"), desc: t("capabilities.aiDesc") },
    { icon: capabilityIcons[1], title: t("capabilities.fuelTitle"), desc: t("capabilities.fuelDesc") },
    { icon: capabilityIcons[2], title: t("capabilities.monitorTitle"), desc: t("capabilities.monitorDesc") },
    { icon: capabilityIcons[3], title: t("capabilities.complianceTitle"), desc: t("capabilities.complianceDesc") },
    { icon: capabilityIcons[4], title: t("capabilities.qualityTitle"), desc: t("capabilities.qualityDesc") },
    { icon: capabilityIcons[5], title: t("capabilities.deployTitle"), desc: t("capabilities.deployDesc") },
  ];

  const steps = [
    { step: "01", title: t("howItWorks.step1Title"), desc: t("howItWorks.step1Desc") },
    { step: "02", title: t("howItWorks.step2Title"), desc: t("howItWorks.step2Desc") },
    { step: "03", title: t("howItWorks.step3Title"), desc: t("howItWorks.step3Desc") },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{t("meta.title")}</title>
        <meta name="description" content={t("meta.description")} />
        <link rel="canonical" href="https://www.cetasol.com/about" />
        <meta property="og:title" content={t("meta.title")} />
        <meta property="og:description" content={t("meta.description")} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cetasol.com/about" />
        <meta property="og:image" content="https://www.cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("meta.title")} />
        <meta name="twitter:description" content={t("meta.description")} />
        <meta name="twitter:image" content="https://www.cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "About", "item": "https://www.cetasol.com/about" }
          ]
        })}</script>
      </Helmet>
      <Header />
      <main className="pb-20">
        {/* Hero — full cover */}
        <section className="relative min-h-[85vh] flex items-end overflow-hidden mb-24">
          <img
            src={aboutHero}
            alt="Maritime monitoring technology in action"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background/90 via-background/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-background/80 via-transparent to-background/40" />

          <div className="relative z-10 container mx-auto px-6 pb-16 pt-32">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, ease }}
              className="max-w-2xl"
            >
              <p className="text-xs font-semibold tracking-widest uppercase text-primary-foreground/70 mb-4">
                {t("whatWeDo.label")}
              </p>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-primary-foreground mb-8 leading-[1.1]">
                {t("whatWeDo.heading")}
              </h1>
              <p className="text-lg text-primary-foreground/80 leading-relaxed mb-6">
                {t("whatWeDo.p1")}
              </p>
              <p className="text-lg text-primary-foreground/80 leading-relaxed">
                {t("whatWeDo.p2")}
              </p>
            </motion.div>
          </div>
        </section>

        {/* Capabilities grid */}
        <section className="bg-surface-light py-20">
          <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease }}
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-foreground mb-4">
              {t("capabilities.label")}
            </p>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-10">
              {t("capabilities.heading")}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 divide-x divide-border">
              {capabilities.map((cap, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="px-8 py-6 text-center"
                >
                  <div className="inline-flex p-2.5 rounded-lg bg-foreground/10 mb-3">
                    <cap.icon size={20} className="text-foreground" />
                  </div>
                  <h3 className="font-display text-base md:text-lg font-semibold text-foreground mb-2">{cap.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{cap.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-20 section-fade">
          <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease }}
          >
            <p className="text-xs font-semibold tracking-widest uppercase text-section-label mb-4">
              {t("howItWorks.label")}
            </p>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-10">
              {t("howItWorks.heading")}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {steps.map((item, i) => (
                <motion.div
                  key={item.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.1 }}
                  className="text-center"
                >
                  <div className="font-display text-5xl font-bold text-primary/20 mb-4">{item.step}</div>
                  <h3 className="font-display text-xl font-semibold text-foreground mb-3">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.6, ease }}
            className="text-center bg-card rounded-xl p-12 border border-border"
          >
            <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-4">
              {t("cta.heading")}
            </h2>
            <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
              {t("cta.description")}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/book-a-demo"
                className="inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground px-8 py-3.5 rounded-md font-medium text-sm hover:bg-primary-hover transition-colors"
              >
                {t("cta.bookDemo")} <ArrowRight size={16} />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center gap-2 border border-[hsl(var(--secondary-border))] text-foreground bg-secondary/18 px-8 py-3.5 rounded-md font-medium text-sm hover:bg-secondary/25 transition-colors"
              >
                {t("cta.exploreSolutions")} <ArrowRight size={16} />
              </Link>
            </div>
          </motion.div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default About;
